// Query hooks. Platform-free: TanStack Query and React, no DOM.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { portalKeys } from './keys';
import { usePortalApi } from './provider';
import type { ListInvoicesParams } from '../services/index';

const CONFIG_STALE_MS = 5 * 60 * 1000;

export function useSummary() {
  const api = usePortalApi();
  return useQuery({ queryKey: portalKeys.summary, queryFn: () => api.summary.get() });
}

export function useInvoices(params: ListInvoicesParams) {
  const api = usePortalApi();
  return useQuery({
    queryKey: portalKeys.invoices(params as Record<string, unknown>),
    queryFn: () => api.invoices.list(params),
    // Keeps the previous page on screen while the next loads, instead of
    // collapsing the list to a spinner on every page change.
    placeholderData: keepPreviousData,
  });
}

export function useInvoice(id: string | undefined) {
  const api = usePortalApi();
  return useQuery({
    queryKey: portalKeys.invoice(id),
    queryFn: () => api.invoices.get(id as string),
    enabled: !!id,
  });
}

export function useInvoiceLogs(id: string | undefined) {
  const api = usePortalApi();
  return useQuery({
    queryKey: portalKeys.invoiceLogs(id),
    queryFn: () => api.invoices.logs(id as string),
    enabled: !!id,
  });
}

export function usePayments() {
  const api = usePortalApi();
  return useQuery({ queryKey: portalKeys.payments, queryFn: () => api.payments.list() });
}

export function usePortalConfig(tenantId: string | undefined) {
  const api = usePortalApi();
  return useQuery({
    queryKey: portalKeys.config(tenantId),
    queryFn: () => api.system.portalConfig(tenantId as string),
    enabled: !!tenantId,
    staleTime: CONFIG_STALE_MS,
  });
}

export interface UseLinkedTenantsOptions {
  /** Mirror the result into app state, for a switcher that renders before fetch. */
  onLoaded?: (
    tenants: Awaited<ReturnType<ReturnType<typeof usePortalApi>['tenants']['listMine']>>
  ) => void;
}

export function useLinkedTenants(options: UseLinkedTenantsOptions = {}) {
  const api = usePortalApi();
  const { onLoaded } = options;

  return useQuery({
    queryKey: portalKeys.linkedTenants,
    queryFn: async () => {
      const tenants = await api.tenants.listMine();
      onLoaded?.(tenants);
      return tenants;
    },
    staleTime: CONFIG_STALE_MS,
  });
}

export function useConfirmTenantLink() {
  const api = usePortalApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (tenantId: string) => api.tenants.confirmLink(tenantId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: portalKeys.linkedTenants }),
  });
}
